import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { AppointmentService } from '@services/appointment.service';
import { Appointment } from 'src/app/models/appointment.model';
import { filter, firstValueFrom, takeUntil } from 'rxjs';
import { SubscribedComponent } from 'src/app/lib/subscribed.component';
import { AppointmentItem } from 'src/app/models/appointment-item.model';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzTypographyComponent } from 'ng-zorro-antd/typography';
import { ThemeService } from '@services/theme.service';
import { NavigationStart, Router } from '@angular/router';
import { LocationService } from '@services/location.service';
import { Location } from 'src/app/models/location.model';
import { environment } from '@env/environment';
import { AppointmentStatusInfoComponent } from '../appointment-status-info/appointment-status-info.component';
import { ErrorService } from '@services/error.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AppointmentStore } from '@services/appointment.store';
import { watchEntity } from 'src/app/lib/entity-watcher.util';
import { AppointmentApiService } from '@services/appointment-api.service';
import { EmptyStateComponent } from '../empty-state/empty-state.component';
import { LocationScheduleComponent } from '../location-schedule/location-schedule.component';
import { AppointmentSummaryComponent } from '../appointment-summary/appointment-summary.component';
import { DateFormatPipe } from '../../../pipes/date-format.pipe';
import { TimeFormatPipe } from '../../../pipes/time-format.pipe';

@Component({
  selector: 'app-cart-content',
  templateUrl: './cart-content.component.html',
  styleUrl: './cart-content.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    NzButtonModule,
    NzIconModule,
    NzTypographyComponent,
    AppointmentStatusInfoComponent,
    TranslateModule,
    EmptyStateComponent,
    LocationScheduleComponent,
    AppointmentSummaryComponent,
    DateFormatPipe,
    TimeFormatPipe,
  ],
})
export class CartContentComponent extends SubscribedComponent implements OnInit {
  @Input() isModal = false;
  /** Read-only, with the remaining balance instead of the total. */
  @Input() selfCheckout = false;
  @Output() closeRequested = new EventEmitter<void>();
  location?: Location;
  appointment: Appointment | null = null;
  continueBtnLabel: string;
  showContinueButton: boolean = false;
  error: string;
  standaloneItems: AppointmentItem[] = [];

  isReschedule = false;

  get isOnlineAppointment(): boolean {
    return this.appointment?.utmSource !== 'in-salon';
  }

  constructor(
    private cdRef: ChangeDetectorRef,
    private locationService: LocationService,
    private appointmentService: AppointmentService,
    private router: Router,
    public themeService: ThemeService,
    private errorService: ErrorService,
    private translate: TranslateService,
    private appointmentStore: AppointmentStore,
    private appointmentApiService: AppointmentApiService,
  ) {
    super();
  }

  async ngOnInit() {
    this.location = this.locationService.getLocation();
    this.watchEntities();

    this.router.events
      .pipe(
        takeUntil(this.destroyed$),
        filter((event: any) => event instanceof NavigationStart),
      )
      .subscribe(() => {
        this.showContinueButton = false;
        this.themeService.setFormSubmitAction(false);
      });
  }

  watchEntities() {
    watchEntity(this.locationService.locationChanged, this.destroyed$, (location) => {
      this.location = location;
      this.cdRef.markForCheck();
    });
    watchEntity(this.appointmentStore.watchAppointment(), this.destroyed$, (appointment) => {
      this.appointment = appointment;
      this.standaloneItems = this.appointmentService.getStandaloneItems(appointment);

      const isTestAppointment =
        appointment?.test || this.location?.uuid === environment.defaultLocationUuid || this.location?.demo;
      const isCheckoutDisabled = this.appointmentService.isCheckoutDisabled(appointment);
      const isOnlineOrderingDisabled = this.appointmentService.isOnlineOrderingDisabled(appointment, this.location);

      if (isTestAppointment) {
        this.themeService.setCartBtnInfoText(this.translate.instant('pages.review.warning-test-appointment-v2'));
        this.themeService.setCartBtnInfoType('danger');
        this.themeService.setCartBtnDisabled(false);
      } else if (isOnlineOrderingDisabled) {
        this.themeService.setCartBtnInfoText(this.translate.instant('pages.review.warning-online-ordering-disabled'));
        this.themeService.setCartBtnInfoType('danger');
        this.themeService.setCartBtnDisabled(true);
      } else if (isCheckoutDisabled) {
        this.themeService.setCartBtnInfoText(this.translate.instant('pages.review.warning-booking-unavailable'));
        this.themeService.setCartBtnInfoType('danger');
        this.themeService.setCartBtnDisabled(true);
      } else {
        this.themeService.setCartBtnInfoText('');
        this.themeService.setCartBtnInfoType('secondary');
        this.themeService.setCartBtnDisabled(false);
      }

      this.cdRef.markForCheck();
    });
    watchEntity(this.themeService.getCartBtnVisibility(), this.destroyed$, (isVisible) => {
      this.showContinueButton = isVisible;
      this.cdRef.markForCheck();
    });
    watchEntity(this.themeService.getCartBtnLabel(), this.destroyed$, (label) => {
      this.continueBtnLabel = label;
      this.cdRef.markForCheck();
    });
    watchEntity(this.themeService.getRescheduleMode(), this.destroyed$, (isReschedule) => {
      this.isReschedule = isReschedule;
      this.cdRef.markForCheck();
    });
  }

  async onDeleteItem(item: AppointmentItem) {
    this.error = undefined;

    try {
      const appointment = await firstValueFrom(this.appointmentApiService.deleteItem(item));
      this.appointmentStore.setAppointment(appointment);

      if (!appointment?.appointmentItems?.length && this.location?.uuid) {
        await this.router.navigate(['/', this.location.uuid, 'services']);
      }
    } catch (e) {
      this.error = this.errorService.extractApiError(e);
    } finally {
      this.cdRef.markForCheck();
    }
  }

  onOpenItemDetails(serviceItemId: number) {
    if (this.isModal) {
      this.closeRequested.emit();
    }

    void this.router.navigate(['/', this.location?.uuid, 'services', 'details', serviceItemId]);
  }

  onContinue() {
    this.themeService.setFormSubmitAction(true);

    if (this.isModal) {
      this.closeRequested.emit();
    }
  }

  setContinueBtnLabel(label: string) {
    this.continueBtnLabel = label;
  }

  // TODO investigate how to better handle the appointment items in the appointment service, so the angular change detection works properly
  // once is done, the standaloneItems methid can be removed and we can call the appointmentService.getStandaloneItems() directly in the ngInit
  // standaloneItems(): AppointmentItem[] {
  //   if (!this.appointment.appointmentItems) return [];
  //   return this.appointmentService.getStandaloneItems();
  // }
}
